import { api } from './api';
import type {
  EvalRun,
  EvalResult,
  TriggeredEvalRun,
  EvalRunTriggerRequest,
  EvalRunListParams,
  PaginatedEvalRunResponse,
  EvalRunComparison,
} from '../types';

// API Response wrapper type (single-object envelope → response.data.data)
interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

export const evalRunService = {
  /** Trigger a run of a suite (HTTP 202, returns the pending run). perm: EVAL_RUN.EXECUTE */
  trigger: async (
    suiteRef: string,
    body: EvalRunTriggerRequest = {}
  ): Promise<TriggeredEvalRun> => {
    const response = await api.post<ApiResponse<TriggeredEvalRun>>(
      `/api/v1/eval-suites/${suiteRef}/run`,
      body
    );
    return response.data.data;
  },

  /** Get one run header by _id or evalRunId. perm: EVAL_RUN.READ */
  getRun: async (runRef: string): Promise<EvalRun> => {
    const response = await api.get<ApiResponse<EvalRun>>(
      `/api/v1/eval-runs/${runRef}`
    );
    return response.data.data;
  },

  /** Re-run one case through the suite's target+grader. Recorded as its own small
   *  run (hidden from the default history). perm: EVAL_RUN.EXECUTE */
  runSingleCase: async (suiteId: string, caseId: string, modelId?: string): Promise<EvalResult> => {
    const response = await api.post<ApiResponse<EvalResult>>(
      `/api/v1/eval-suites/${suiteId}/cases/${caseId}/run-once`,
      modelId ? { modelId } : {}
    );
    return response.data.data;
  },

  /** Stop a pending/running run. In-flight cases finish; queued ones are skipped.
   *  perm: EVAL_RUN.EXECUTE */
  cancelRun: async (runRef: string): Promise<EvalRun> => {
    const response = await api.post<ApiResponse<EvalRun>>(
      `/api/v1/eval-runs/${runRef}/cancel`
    );
    return response.data.data;
  },

  /** Get every per-case result for a run (eval_result rows). perm: EVAL_RUN.READ */
  getRunResults: async (runRef: string): Promise<EvalResult[]> => {
    const response = await api.get<ApiResponse<EvalResult[]>>(
      `/api/v1/eval-runs/${runRef}/results`
    );
    return response.data.data;
  },

  /** Compare a run with another, case by case (default: the suite's previous full run).
   *  perm: EVAL_RUN.READ */
  compareRuns: async (runRef: string, baseRef?: string | null): Promise<EvalRunComparison> => {
    const response = await api.get<ApiResponse<EvalRunComparison>>(
      `/api/v1/eval-runs/${runRef}/compare`,
      { params: baseRef ? { base: baseRef } : undefined }
    );
    return response.data.data;
  },

  /** List runs (paginated, headers only). perm: EVAL_RUN.READ */
  listRuns: async (
    params?: EvalRunListParams
  ): Promise<PaginatedEvalRunResponse> => {
    const response = await api.get<PaginatedEvalRunResponse>(
      '/api/v1/eval-runs',
      { params }
    );
    return response.data;
  },
};
